import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
  GitMerge,
  RefreshCw,
  Save,
  ShieldAlert,
} from "lucide-react";
import {
  getHumanInLoopError,
  humanInLoopApi,
} from "../../api/humanInLoopApi";
import StopAssignmentPlanner, {
  buildAssignmentPlan,
  validateStopOrder,
} from "./StopAssignmentPlanner";
import "./humanInLoop.css";

const managerId = () =>
  localStorage.getItem("managerMobile") || localStorage.getItem("managerId") || "MANAGER";

const sortedUniqueTourIds = (tourIds = []) =>
  [...new Set(tourIds.map(Number).filter((id) => Number.isInteger(id) && id > 0))]
    .sort((left, right) => left - right);

const sameStopSet = (left = [], right = []) => {
  const leftIds = left.map((stop) => String(stop.stop_id)).sort();
  const rightIds = right.map((stop) => String(stop.stop_id)).sort();
  return leftIds.length === rightIds.length && leftIds.every((value, index) => value === rightIds[index]);
};

const MergeToursModal = ({ isOpen, tourIds, onClose, onSaved }) => {
  const selectedTourIds = useMemo(() => sortedUniqueTourIds(tourIds), [tourIds]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [stops, setStops] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [primaryTourId, setPrimaryTourId] = useState("");
  const [sequenceLock, setSequenceLock] = useState("RELATIVE");
  const sessionRef = useRef(null);
  const closedRef = useRef(false);

  const releaseSession = useCallback(async () => {
    const sessionId = sessionRef.current;
    sessionRef.current = null;
    if (!sessionId) return;
    try {
      await humanInLoopApi.release(sessionId);
    } catch (_) {
      // The server-side session TTL is the final fallback.
    }
  }, []);

  const loadPreview = useCallback(async () => {
    if (!isOpen) return;
    if (selectedTourIds.length < 2) {
      setError("Select at least two active, non-dummy tours before merging.");
      return;
    }
    setLoading(true);
    setError("");
    setPreview(null);
    setDrivers([]);
    setStops([]);
    setAssignments([]);
    closedRef.current = false;
    try {
      const [driversResponse, previewResponse] = await Promise.all([
        humanInLoopApi.getDrivers(),
        humanInLoopApi.previewMergeTours({ source_tour_ids: selectedTourIds }),
      ]);
      if (closedRef.current) return;
      const data = previewResponse.data;
      const realStops = (data.stops || []).filter((stop) => Number(stop.booking_id) !== 1);
      setDrivers(driversResponse.data || []);
      setPreview(data);
      setStops(realStops);
      setAssignments(buildAssignmentPlan(realStops));
      setPrimaryTourId(String(data.primary_tour_id || selectedTourIds[0]));
      const existingLock = realStops.find((stop) => stop.sequence_lock)?.sequence_lock;
      setSequenceLock(String(existingLock || "RELATIVE").toUpperCase());
      if (!data.can_merge) {
        const blocked = (data.blocked_stops || [])
          .map((stop) => `${stop.stop_id} (${stop.reason})`)
          .join(", ");
        setError(`The selected tours contain stops that cannot be moved: ${blocked}`);
      }
    } catch (requestError) {
      setError(getHumanInLoopError(requestError, "Unable to load the merge planner."));
    } finally {
      setLoading(false);
    }
  }, [isOpen, selectedTourIds]);

  useEffect(() => {
    void loadPreview();
    return () => {
      closedRef.current = true;
      void releaseSession();
    };
  }, [loadPreview, releaseSession]);

  const handleSave = async () => {
    if (!preview || !preview.can_merge || !stops.length || !assignments.length) return;
    const orderError = validateStopOrder(stops);
    if (orderError) {
      setError(orderError);
      return;
    }

    setSaving(true);
    setError("");
    try {
      // Re-read both the selected tours and driver versions immediately before
      // locking, so the optimistic versions submitted below are authoritative.
      const [freshPreviewResponse, freshDriversResponse] = await Promise.all([
        humanInLoopApi.previewMergeTours({ source_tour_ids: selectedTourIds }),
        humanInLoopApi.getDrivers(),
      ]);
      const freshPreview = freshPreviewResponse.data;
      const freshDrivers = freshDriversResponse.data || [];
      if (!freshPreview.can_merge) {
        throw new Error("A selected stop became started, completed, fixed or fully locked.");
      }
      const assignmentVersionChanged = assignments.some((assignment) =>
        Number(freshPreview.expected_assignment_versions?.[String(assignment.bookingId)] || 0) !==
        Number(assignment.assignmentVersion || 0));
      if (!sameStopSet(stops, freshPreview.stops || []) || assignmentVersionChanged) {
        const latestStops = (freshPreview.stops || []).filter((stop) => Number(stop.booking_id) !== 1);
        setPreview(freshPreview);
        setStops(latestStops);
        setAssignments(buildAssignmentPlan(latestStops));
        throw new Error(
          "The selected tours or booking assignments changed while you were arranging them. The latest state is now loaded; review the plan and save again.",
        );
      }

      const targetVehicleIds = assignments
        .map((assignment) => assignment.targetVehicleId)
        .filter(Boolean)
        .map(String);
      const affectedVehicleIds = [...new Set([
        ...(freshPreview.source_vehicle_ids || []).map(String),
        ...targetVehicleIds,
      ])];
      const freshDriverByVehicle = new Map(
        freshDrivers.map((driver) => [String(driver.vehicle_id), driver]),
      );
      const expectedTourVersions = {};
      for (const vehicleId of affectedVehicleIds) {
        expectedTourVersions[vehicleId] = freshPreview.expected_tour_versions?.[vehicleId] != null
          ? Number(freshPreview.expected_tour_versions[vehicleId])
          : Number(freshDriverByVehicle.get(vehicleId)?.tour_version || 0);
      }

      const sessionResponse = await humanInLoopApi.createEditSession({
        manager_id: managerId(),
        manager_name: "Operations Manager",
        vehicle_ids: affectedVehicleIds,
        booking_ids: freshPreview.booking_ids.map(String),
        base_tour_versions: expectedTourVersions,
        ttl_sec: 300,
        reason: "MANUAL_TOUR_MERGE",
      });
      sessionRef.current = sessionResponse?.session?.session_id;
      if (!sessionRef.current) {
        throw new Error("The allocator did not return an edit session ID.");
      }

      await humanInLoopApi.mergeTours({
        edit_session_id: sessionRef.current,
        source_tour_ids: selectedTourIds,
        primary_tour_id: Number(primaryTourId || selectedTourIds[0]),
        expected_tour_versions: expectedTourVersions,
        expected_assignment_versions: freshPreview.expected_assignment_versions,
        booking_assignments: assignments.map((assignment) => ({
          booking_id: Number(assignment.bookingId),
          target_vehicle_id: assignment.targetVehicleId == null
            ? null
            : String(assignment.targetVehicleId),
          assignment_lock: assignment.targetVehicleId == null
            ? "NONE"
            : String(assignment.assignmentLock || "VEHICLE").toUpperCase(),
        })),
        ordered_stop_ids: stops.map((stop) => String(stop.stop_id)),
        sequence_lock: sequenceLock,
        manager_id: managerId(),
        reason_code: "MANUAL_TOUR_MERGE",
        reason_text: `Merged tours ${selectedTourIds.join(", ")} with individual stop ordering and multi-driver assignment`,
      });
      sessionRef.current = null;
      onSaved?.();
      onClose?.();
    } catch (requestError) {
      await releaseSession();
      const status = requestError?.response?.status;
      setError(
        status === 409
          ? "A selected tour, booking or driver plan changed before the merge was committed. Reload the merge planner and review the latest state."
          : getHumanInLoopError(requestError, requestError?.message || "Unable to merge the tours."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async () => {
    closedRef.current = true;
    await releaseSession();
    onClose?.();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="xl" backdrop="static" scrollable>
      <ModalHeader toggle={handleClose}>
        <span className="hitl-title-with-icon">
          <GitMerge size={20} /> Merge tours, arrange individual stops and assign drivers
        </span>
      </ModalHeader>
      <ModalBody>
        {loading && (
          <div className="hitl-centre">
            <RefreshCw className="hitl-spin" /> Loading selected tours…
          </div>
        )}
        {error && (
          <div className="hitl-alert">
            <ShieldAlert size={18} /> {error}
          </div>
        )}
        {!loading && preview && (
          <>
            <div className="hitl-merge-summary">
              <div><strong>Selected tours</strong><span>{preview.source_tour_ids.join(", ")}</span></div>
              <div><strong>Bookings</strong><span>{preview.booking_ids.length}</span></div>
              <div><strong>Stops</strong><span>{preview.stops.length}</span></div>
              <div><strong>Source drivers</strong><span>{preview.source_vehicle_ids.length}</span></div>
            </div>
            <div className="hitl-primary-tour-row">
              <label>Preferred retained source tour</label>
              <select
                value={primaryTourId}
                onChange={(event) => setPrimaryTourId(event.target.value)}
                disabled={saving}
              >
                {(preview.selected_tours || []).map((tour) => (
                  <option key={tour.id} value={tour.id}>
                    Tour #{tour.id} — {tour.driver_name || "Unnamed driver"} — {tour.mobile_number || "No phone number"}
                  </option>
                ))}
              </select>
              <span>
                This is only the preferred source tour to retain. Each booking can still be sent to a different driver or returned to Trips.
              </span>
            </div>
            <StopAssignmentPlanner
              stops={stops}
              setStops={setStops}
              assignments={assignments}
              setAssignments={setAssignments}
              drivers={drivers}
              sequenceLock={sequenceLock}
              setSequenceLock={setSequenceLock}
              disabled={saving || !preview.can_merge}
              onError={setError}
            />
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="hitl-secondary-button" onClick={handleClose} disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          className="hitl-primary-button"
          onClick={handleSave}
          disabled={loading || saving || !preview?.can_merge || !stops.length}
        >
          {saving ? <RefreshCw className="hitl-spin" size={17} /> : <Save size={17} />}
          {saving ? "Saving…" : "Save merged assignments and stop sequence"}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default MergeToursModal;
